"use client";

import {
  LineChart,
  Line,
  AreaChart,
  Area,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";
import { palette } from "@/lib/colors";

interface ChartProps {
  data: any[];
  height?: number;
}

interface CustomTooltipProps {
  active?: boolean;
  payload?: any[];
  label?: string;
}

function CustomTooltip({ active, payload, label }: CustomTooltipProps) {
  if (!active || !payload || !payload.length) return null;

  return (
    <div className="rounded-lg border border-[--color-border] bg-[--color-bg] p-3 shadow-sm">
      <div className="mb-2 font-mono text-xs font-medium text-[--color-text-muted]">
        {label}
      </div>
      {payload.map((entry: any, index: number) => (
        <div key={index} className="flex items-center gap-2">
          <div
            className="h-2 w-2 rounded-full"
            style={{ backgroundColor: entry.color }}
          />
          <span className="text-sm font-medium text-[--color-text-secondary]">
            {entry.name}:
          </span>
          <span className="text-sm font-semibold text-[--color-text]">
            {entry.value}
          </span>
        </div>
      ))}
    </div>
  );
}

const axisProps = {
  stroke: palette.axis,
  style: { fontSize: "12px", fontFamily: "var(--font-family-mono)" },
  tickLine: false as const,
  axisLine: { stroke: palette.grid },
};

export function IndexedLineChart({ data, height = 300 }: ChartProps) {
  return (
    <ResponsiveContainer width="100%" height={height}>
      <LineChart data={data}>
        <CartesianGrid
          strokeDasharray="3 3"
          stroke={palette.grid}
          vertical={false}
        />
        <XAxis dataKey="month" {...axisProps} />
        <YAxis {...axisProps} />
        <Tooltip content={<CustomTooltip />} />
        <Legend
          wrapperStyle={{ fontSize: "12px", color: palette.legend }}
          iconType="line"
        />
        <Line
          type="monotone"
          dataKey="efficiency"
          stroke={palette.teal}
          strokeWidth={2.25}
          dot={false}
          activeDot={{ r: 4, fill: palette.teal }}
        />
        <Line
          type="monotone"
          dataKey="spend"
          stroke={palette.amber}
          strokeWidth={2}
          strokeDasharray="5 4"
          dot={false}
          activeDot={{ r: 4, fill: palette.amber }}
        />
      </LineChart>
    </ResponsiveContainer>
  );
}

export function MultiLineChart({ data, height = 300 }: ChartProps) {
  return (
    <ResponsiveContainer width="100%" height={height}>
      <LineChart data={data}>
        <CartesianGrid
          strokeDasharray="3 3"
          stroke={palette.grid}
          vertical={false}
        />
        <XAxis dataKey="month" {...axisProps} />
        <YAxis {...axisProps} />
        <Tooltip content={<CustomTooltip />} />
        <Legend
          wrapperStyle={{ fontSize: "12px", color: palette.legend }}
          iconType="line"
        />
        <Line
          type="monotone"
          dataKey="meta"
          stroke={palette.sky}
          strokeWidth={2.25}
          dot={false}
          activeDot={{ r: 4, fill: palette.sky }}
        />
        <Line
          type="monotone"
          dataKey="google"
          stroke={palette.teal}
          strokeWidth={2}
          strokeDasharray="5 4"
          dot={false}
          activeDot={{ r: 4, fill: palette.teal }}
        />
      </LineChart>
    </ResponsiveContainer>
  );
}

export function AreaChartComponent({ data, height = 300 }: ChartProps) {
  return (
    <ResponsiveContainer width="100%" height={height}>
      <AreaChart data={data}>
        <defs>
          <linearGradient id="colorPaid" x1="0" y1="0" x2="0" y2="1">
            <stop offset="5%" stopColor={palette.sky} stopOpacity={0.35} />
            <stop offset="95%" stopColor={palette.sky} stopOpacity={0} />
          </linearGradient>
          <linearGradient id="colorCRM" x1="0" y1="0" x2="0" y2="1">
            <stop offset="5%" stopColor={palette.teal} stopOpacity={0.3} />
            <stop offset="95%" stopColor={palette.teal} stopOpacity={0} />
          </linearGradient>
          <linearGradient id="colorOrganic" x1="0" y1="0" x2="0" y2="1">
            <stop offset="5%" stopColor={palette.slate} stopOpacity={0.28} />
            <stop offset="95%" stopColor={palette.slate} stopOpacity={0} />
          </linearGradient>
        </defs>
        <CartesianGrid
          strokeDasharray="3 3"
          stroke={palette.grid}
          vertical={false}
        />
        <XAxis dataKey="month" {...axisProps} />
        <YAxis {...axisProps} />
        <Tooltip content={<CustomTooltip />} />
        <Legend
          wrapperStyle={{ fontSize: "12px", color: palette.legend }}
          iconType="rect"
        />
        <Area
          type="monotone"
          dataKey="paid"
          stackId="1"
          stroke={palette.sky}
          fill="url(#colorPaid)"
        />
        <Area
          type="monotone"
          dataKey="crm"
          stackId="1"
          stroke={palette.teal}
          fill="url(#colorCRM)"
        />
        <Area
          type="monotone"
          dataKey="organic"
          stackId="1"
          stroke={palette.slate}
          fill="url(#colorOrganic)"
        />
      </AreaChart>
    </ResponsiveContainer>
  );
}

export function BarChartComponent({ data, height = 300 }: ChartProps) {
  return (
    <ResponsiveContainer width="100%" height={height}>
      <BarChart data={data}>
        <CartesianGrid
          strokeDasharray="3 3"
          stroke={palette.grid}
          vertical={false}
        />
        <XAxis dataKey="name" {...axisProps} />
        <YAxis {...axisProps} />
        <Tooltip content={<CustomTooltip />} />
        <Bar dataKey="value" fill={palette.teal} radius={[4, 4, 0, 0]} />
      </BarChart>
    </ResponsiveContainer>
  );
}
